import { Calendar, Home, Sailboat, Ship, Users, Utensils } from "lucide-react"

import { SidebarAppearance } from "@/components/Common/Appearance"
import { Logo } from "@/components/Common/Logo"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar"
import useAuth from "@/hooks/useAuth"
import { type Item, Main } from "./Main"
import { canViewKayak, canViewKitchen, canViewWaterTaxi } from "./sidebarAccess"
import { User } from "./User"

const baseItems: Item[] = [
  { icon: Home, title: "Dashboard", path: "/dashboard" },
  { icon: Calendar, title: "Bookings", path: "/bookings" },
]

const taxiStaffItems: Item[] = [
  { icon: Ship, title: "Water Taxi", path: "/calendar/taxi" },
]

const kitchenStaffItems: Item[] = [
  { icon: Utensils, title: "Kitchen", path: "/calendar/kitchen" },
]

const kayakStaffItems: Item[] = [
  { icon: Sailboat, title: "Kayak Rentals", path: "/calendar/kayak" },
]

/**
 * Check if user should see the manager dashboard link.
 */
function canViewDashboard(
  user: ReturnType<typeof useAuth>["user"] | null,
): boolean {
  if (!user) return false
  const role = String(user.role ?? "").toLowerCase()
  return (
    role === "manager" ||
    role === "admin" ||
    role === "superuser" ||
    !!user.is_superuser
  )
}

/**
 * Check if user should see the admin link.
 */
function canViewAdmin(
  user: ReturnType<typeof useAuth>["user"] | null,
): boolean {
  // Always return false if no user data yet (waiting for auth to load)
  if (!user) return false
  const role = String(user.role ?? "").toLowerCase()
  // Show admin for superusers, admins, and managers
  return (
    role === "superuser" ||
    role === "admin" ||
    role === "manager" ||
    !!user.is_superuser
  )
}

/**
 * Assemble the sidebar navigation items permitted for the provided user.
 *
 * If `user` is `null` (authentication not yet loaded), returns the full set of items (base, water taxi, kitchen, kayak) plus the Admin item so the UI can render before auth resolves.
 *
 * @param user - Current authenticated user object or `null` if not loaded
 * @returns The array of sidebar `Item`s that should be shown for the user
 */
function getSidebarItems(
  user: ReturnType<typeof useAuth>["user"] | null,
): Item[] {
  // If no user data yet, show all items (will be filtered once auth loads)
  if (!user) {
    return [
      ...baseItems,
      ...taxiStaffItems,
      ...kitchenStaffItems,
      ...kayakStaffItems,
      { icon: Users, title: "Admin", path: "/admin" },
    ]
  }

  const showDashboard = canViewDashboard(user)
  const showWaterTaxi = canViewWaterTaxi(user)
  const showKitchen = canViewKitchen(user)
  const showKayak = canViewKayak(user)
  const showAdmin = canViewAdmin(user)

  // Build items based on permissions
  let items: Item[] = []
  if (showDashboard) {
    items = [...items]
  }
  items = [...items, ...baseItems]
  if (showWaterTaxi) {
    items = [...items, ...taxiStaffItems]
  }
  if (showKitchen) {
    items = [...items, ...kitchenStaffItems]
  }
  if (showKayak) {
    items = [...items, ...kayakStaffItems]
  }
  if (showAdmin) {
    items = [...items, { icon: Users, title: "Admin", path: "/admin" }]
  }
  return items
}

/**
 * Render the application sidebar with navigation items and user controls.
 *
 * The sidebar includes the Dashboard and Bookings navigation items and appends
 * an Admin item when the current authenticated user has `is_superuser` set to
 * true or has role "superuser"/"admin". For taxi/dock staff, a Water Taxi
 * Calendar link is shown. The header shows the responsive logo, the content
 * renders the main navigation using the computed items, and the footer contains
 * appearance controls and the current user's UI.
 *
 * @returns The sidebar React element configured with navigation items and user controls.
 */
export function AppSidebar() {
  const { user: currentUser } = useAuth()

  const items = getSidebarItems(currentUser)

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="px-4 py-6 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:items-center">
        <Logo variant="responsive" />
      </SidebarHeader>
      <SidebarContent>
        <Main items={items} />
      </SidebarContent>
      <SidebarFooter>
        <SidebarAppearance />
        <User user={currentUser} />
      </SidebarFooter>
    </Sidebar>
  )
}
